import { useEffect, useRef, useState } from 'react';
import { Box, Button, Stack } from '@mui/material';
import i18n from '../../i18n';
import apiManage from '../../request';
import GlobalNotification from '../../component/common/snackbar';
import CardView from '../../component/card/card-view';
import { MobxObjectsManager } from '../mobx-objs/mobx-manager';
import DialogComponent from '../../component/dialog/dialog.component';
import PdfPreviewDialog from '../../component/pdf-preview-dialog';
import { TopBarComponent } from '../../component/top-bar/top-bar.component';
import { TopBarState } from '../../component/top-bar/top-bar-state';
import PasswordInput from '../../component/form/password-input';
import { ShareLinkValidate } from '../../request/api/restaurant';
import ButtonComponent from '../../component/button/button.component';
import LoadingOverlayButton from '../../component/button/loading-overlay-button.component';
import { useTranslation } from 'react-i18next';
import './fiskaly-settiing-user.page.scss';

// 顶部栏数据
const topBarState = MobxObjectsManager.get(TopBarState);

export default function FiskalySetting() {
    useTranslation();
    // 密码弹窗
    const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
    // 密码
    const [password, setPassword] = useState<string>('');
    // 上传文件loading
    const [fileLoading, setFileLoading] = useState(false)
    // 预览
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string>('');
    // 文件输入
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    // token
    const [token, setToken] = useState<string>('');

    // data 
    const [data, setData] = useState<ShareLinkValidate>({});

    useEffect(() => {
        topBarState.updateFiskalySettingTitle();
        // 从链接中获取token
        const hash = window.location.hash.substring(1);
        const query = hash.split('?')[1];
        if (query) {
            const params = new URLSearchParams(query);
            setToken(params.get('t') || '');
            setOpenPasswordDialog(true)
        } else {
            GlobalNotification.error(i18n.t('fiskaly_setting_invalid_link'));
        }

    }, [])

    /**
     * 初始化隐私协议
     * @param value 
     */
    const initAgreement = async () => {
        try {
            if (!password) {
                GlobalNotification.error(i18n.t('fiskaly_setting_placeholder_password'));
                return;
            }
            const res = await apiManage.get_share_link({
                password,
                t: token
            });
            // status 为 ok 表示分享链接有效且协议流程就绪，不再依赖 restaurant_agreement 字段是否存在
            if (res && !res.error && res?.status === 'ok') {
                // 后端可能返回仅含空格的协议 URL，入库前 trim，空白视为无效空串
                setData({
                    ...res,
                    restaurant_agreement: (res.restaurant_agreement || '').trim(),
                    restaurant_unsigned_agreement: (res.restaurant_unsigned_agreement || '').trim(),
                });
                setOpenPasswordDialog(false);
            } else {
                setData({});
                GlobalNotification.error(i18n.t('fiskaly_setting_password_or_link_invalid'));
            }
        } catch (_e) {
            GlobalNotification.error(i18n.t('fiskaly_setting_password_or_link_invalid'));
        }
    };

    /**
     * 下载纳税人协议 PDF；协议 URL 为空或仅空白时提示联系商家。
     * @param value - 协议文件 URL（未签名或已签名）
     */
    const handleDownload = async (value: string) => {
        try {
            // 协议未生成时后端可能返回空字符串，trim 后拦截避免无效 fetch
            if (!value.trim()) {
                GlobalNotification.error(i18n.t('fiskaly_agreement_not_generated_contact_merchant'));
                return;
            }
            // 1. 获取 PDF 数据
            const response = await fetch(value);
            if (!response.ok) throw new Error('网络错误');

            // 2. 转为 Blob
            const blob = await response.blob();

            // 3. 创建临时下载链接
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = 'Agreement'; // 指定文件名

            // 4. 触发点击
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // 5. 释放内存
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('PDF 下载失败:', error);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        }
    }

    /**
     * 点击上传；未签名协议未生成时提示联系商家，不打开文件选择。
     */
    const handleClickUpload = () => {
        // 未签名协议 URL 为空表示注册流程未完成，无法上传已签协议（入库时已 trim）
        if (!data.restaurant_unsigned_agreement) {
            GlobalNotification.error(i18n.t('fiskaly_agreement_not_generated_contact_merchant'));
            return;
        }
        fileInputRef.current?.click();
    };

    /**
     * 上传文件
     */
    const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        // 允许重复选择同一文件
        event.target.value = '';
        if (!file) return;
        if (file.type !== 'application/pdf') {
            GlobalNotification.error(i18n.t('input_rule_text_please_upload_the_correct_picture_format_support_only_x', { x: 'PDF' }));
            return;
        }
        try {
            setFileLoading(true);
            const res = await apiManage.upload_agreement?.({
                file,
                t: token
            });
            if (res && !res.error && res?.url) {
                GlobalNotification.success(i18n.t('global_text_operate_success'));
                // 重新获取隐私协议
                initAgreement();
            } else {
                const details = res?.result?.details;
                const message = typeof details === 'string' ? details : i18n.t('global_text_operate_failure');
                GlobalNotification.error(message);
            }

        } catch (_e) {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } finally {
            setFileLoading(false);
        }
    };

    /**
     * 打开 PDF 预览弹窗；协议 URL 为空时与下载使用相同提示。
     * @param url - 协议 PDF 文件 URL
     */
    const openPreview = (url: string) => {
        if (!url.trim()) {
            GlobalNotification.error(i18n.t('fiskaly_agreement_not_generated_contact_merchant'));
            return;
        }
        setPreviewUrl(url.trim());
        setPreviewOpen(true);
    };




    return (
        <div className='app-shell'>

            {
                // 密码校验通过后，以 status 判断协议页是否可展示
                data.status === 'ok' ?
                    <div className='content-page fiskaly-upload-guest-page'>
                        <TopBarComponent showAvatar={false} />
                        <div className='content-page-container'>
                            <Box sx={{ maxWidth: 800 }}>
                                <CardView
                                    title={i18n.t('general_text_fiskaly_agreement')}
                                    contentRender={(
                                        <div>
                                            {i18n.t('restaurant_info_page_status')}:  {data.fiskaly_upload_agreemen === '1' ? i18n.t('fiskaly_setting_status_uploaded') : i18n.t('fiskaly_setting_status_not_uploaded')}
                                        </div>
                                    )}
                                    actionRender={(<Stack direction='row' spacing={2} alignItems='center' mb={2}>
                                        <Button
                                            variant='contained'
                                            onClick={() => handleDownload(data.restaurant_unsigned_agreement || '')}
                                        >
                                            {i18n.t('fiskaly_page_button_download_unsigned_agreement')}
                                        </Button>
                                        <LoadingOverlayButton
                                            variant='contained'
                                            loading={fileLoading}
                                            onClick={handleClickUpload}
                                        >
                                            {i18n.t('general_text_upload')}
                                        </LoadingOverlayButton>
                                        {
                                            data.fiskaly_upload_agreemen === '1' &&
                                            <Button
                                                variant='contained'
                                                onClick={() => handleDownload(data.restaurant_agreement || '')}
                                            >
                                                {i18n.t('fiskaly_page_button_download_signed_agreement')}
                                            </Button>
                                        }
                                        <Button
                                            variant='outlined'
                                            onClick={() => openPreview(data.restaurant_agreement || data.restaurant_unsigned_agreement || '')}
                                        >
                                            {i18n.t('general_text_preview')}
                                        </Button>
                                    </Stack>
                                    )}
                                    style={{ marginBottom: 16 }}
                                />
                                <input
                                    ref={fileInputRef}
                                    type='file'
                                    accept='application/pdf'
                                    style={{ display: 'none' }}
                                    onChange={handleUpload}
                                />

                                <PdfPreviewDialog
                                    open={previewOpen}
                                    onClose={() => setPreviewOpen(false)}
                                    url={previewUrl}
                                />
                            </Box >
                        </div>
                    </div>
                    :
                    null
            }

            <DialogComponent
                open={openPasswordDialog}
                fullWidth
                maxWidth='xs'
                heightSize='xs'
                defaultHeaderProps={{ title: i18n.t('login_page_text_password'), isHiddenCloseIcon: true }}
                footer={
                    <>
                        <ButtonComponent
                            color='primary'
                            variant='contained'
                            handleClick={initAgreement}
                        >{i18n.t('google_text_confirm')}</ButtonComponent>

                    </>
                }
            >
                <PasswordInput
                    value={password}
                    label={i18n.t('login_page_text_password')}
                    onChange={(value) => {
                        setPassword(value)
                    }}
                    placeholder={i18n.t('fiskaly_setting_placeholder_password')}
                    regularType='name'
                />
            </DialogComponent>
        </div>
    );
}


